import dayjs from 'dayjs';
import { nanoid } from 'nanoid';

import { DATE_FORMAT } from '@webapp/common/lib/date';
import { QuestionType } from '@webapp/common/resources/survey';

export enum ComparisonPreset {
    PREV_SIMILAR = 'prevSimilar',
    PREV_MONTH = 'prevMonth',
    PREV_QUARTER = 'prevQuarter',
    PREV_YEAR = 'prevYear',
    ALL_TIME = 'allTime',
    CUSTOM = 'custom',
}

export interface ComparisonPeriod {
    id: string; // nanoid, стабильный ключ для стора и цвета
    dateFrom: string; // YYYY-MM-DD
    dateTo: string;
    name?: string; // пользовательское название; по умолчанию «Период N»
    preset?: ComparisonPreset;
    clipped?: boolean; // диапазон обрезан датой создания опроса
}

export interface ComparisonDateRange {
    dateFrom: string;
    dateTo: string;
}

export const BASE_PERIOD_ID = 'base';
export const MAX_COMPARE_PERIODS = 4; // + базовый = 5

export const PRESET_LABELS: Record<ComparisonPreset, string> = {
    [ComparisonPreset.PREV_SIMILAR]: 'Предыдущий период той же длительности',
    [ComparisonPreset.PREV_MONTH]: 'Аналогичный период прошлого месяца',
    [ComparisonPreset.PREV_QUARTER]: 'Аналогичный период прошлого квартала',
    [ComparisonPreset.PREV_YEAR]: 'Аналогичный период прошлого года',
    [ComparisonPreset.ALL_TIME]: 'За всё время',
    [ComparisonPreset.CUSTOM]: 'Другой период',
};

export const ALL_TIME_PERIOD_NAME = 'За всё время';

// Псевдопериод для типов без сравнения: данные «за всё время опроса».
export const ALL_TIME_PERIOD_ID = 'allTimeReport';

// Типы вопросов, для которых сравнение периодов недоступно — показываем
// данные за всё время опроса + пометку.
export const NO_COMPARISON_TYPES: Set<QuestionType> = new Set([
    QuestionType.TEXT_BLOCK,
    QuestionType.FILE_UPLOAD,
    QuestionType.NAME,
    QuestionType.EMAIL,
    QuestionType.PHONE,
    QuestionType.DATETIME,
    QuestionType.GROUP_FREE_ANSWERS,
    QuestionType.MATRIX_DROPDOWN_LIST,
    QuestionType.MATRIX_TEXT_ANSWER,
]);

export const formatPeriodDate = (date: string): string => dayjs(date).format(DATE_FORMAT);

export const getPeriodLengthDays = (period: ComparisonDateRange): number =>
    dayjs(formatPeriodDate(period.dateTo)).diff(dayjs(formatPeriodDate(period.dateFrom)), 'day') + 1;

// «Аналогичный период прошлого месяца/года» сравнивает базу с календарным отрезком,
// поэтому при базе длиннее месяца/года пресет недоступен: период той же длины
// захватил бы лишние дни и перестал быть «аналогичным».
export const isPresetAvailable = ({
    base,
    preset,
}: {
    base: ComparisonDateRange;
    preset: ComparisonPreset;
}): boolean => {
    const start = dayjs(formatPeriodDate(base.dateFrom));
    const end = dayjs(formatPeriodDate(base.dateTo));

    if (preset === ComparisonPreset.PREV_MONTH) {
        return !end.isAfter(start.add(1, 'month').subtract(1, 'day'), 'day');
    }

    if (preset === ComparisonPreset.PREV_YEAR) {
        return !end.isAfter(start.add(1, 'year').subtract(1, 'day'), 'day');
    }

    return true;
};

export const createComparisonPeriod = (
    period: ComparisonDateRange,
    preset?: ComparisonPreset,
    clipped = false,
): ComparisonPeriod => ({
    id: nanoid(),
    dateFrom: formatPeriodDate(period.dateFrom),
    dateTo: formatPeriodDate(period.dateTo),
    preset,
    clipped,
});

const clampToMinDate = (period: ComparisonPeriod, minDate?: string): ComparisonPeriod => {
    if (!minDate) {
        return period;
    }

    const min = dayjs(formatPeriodDate(minDate));

    if (!min.isValid() || !dayjs(period.dateFrom).isBefore(min)) {
        return period;
    }

    return {
        ...period,
        dateFrom: min.format(DATE_FORMAT),
        clipped: true,
    };
};

// Все пресеты считаются по числу дней базового периода (длина сохраняется) и всегда
// от базового периода: базовый 01.07–15.07 (15 дней) → prevSimilar = 16.06–30.06,
// prevMonth = 01.06–15.06, prevQuarter = 01.04–15.04, prevYear = 01.07.2025–15.07.2025.
export const getPresetPeriod = ({
    base,
    preset,
    minDate,
}: {
    base: ComparisonDateRange;
    preset: ComparisonPreset;
    minDate?: string;
}): ComparisonPeriod => {
    if (preset === ComparisonPreset.ALL_TIME) {
        return createAllTimePeriod(minDate);
    }

    const length = Math.max(1, getPeriodLengthDays(base));
    const baseStart = dayjs(formatPeriodDate(base.dateFrom));

    let start: dayjs.Dayjs;

    switch (preset) {
        case ComparisonPreset.PREV_MONTH:
            start = baseStart.subtract(1, 'month');
            break;
        case ComparisonPreset.PREV_QUARTER:
            start = baseStart.subtract(3, 'month');
            break;
        case ComparisonPreset.PREV_YEAR:
            start = baseStart.subtract(1, 'year');
            break;
        case ComparisonPreset.PREV_SIMILAR:
        default:
            start = baseStart.subtract(length, 'day');
            break;
    }

    const dateFrom = start.format(DATE_FORMAT);
    const dateTo = start.add(length - 1, 'day').format(DATE_FORMAT);

    return clampToMinDate(createComparisonPeriod({ dateFrom, dateTo }, preset), minDate);
};

// Общий период «За всё время»: от даты создания опроса до сегодня.
export const createAllTimePeriod = (minDate?: string): ComparisonPeriod => {
    const dateTo = dayjs().format(DATE_FORMAT);

    if (!minDate) {
        return createComparisonPeriod({ dateFrom: dateTo, dateTo }, ComparisonPreset.ALL_TIME);
    }

    const period = createComparisonPeriod({ dateFrom: formatPeriodDate(minDate), dateTo }, ComparisonPreset.ALL_TIME);

    return {
        ...period,
        name: ALL_TIME_PERIOD_NAME,
    };
};

// Название периода в результатах: пользовательское или «Период N» (N с единицы).
export const getPeriodName = (period: { name?: string }, index = 0): string => period.name || `Период ${index + 1}`;

export enum DeltaKind {
    ABSOLUTE = 'absolute',
    POINTS = 'points',
    RANK = 'rank',
}

export interface Delta {
    value: number;
    kind: DeltaKind;
    direction: -1 | 0 | 1;
}

// База сравнения — базовый период. Для показателей diff = current - base, для
// позиций (rank) — base - current, чтобы «упал с 1 на 3» давало отрицательную Δ.
export const calcDelta = (current: number, base: number, kind: DeltaKind): Delta => {
    if (!isFinite(current) || !isFinite(base)) {
        return { value: 0, kind, direction: 0 };
    }

    const raw = kind === DeltaKind.RANK ? base - current : current - base;
    const value = kind === DeltaKind.RANK ? Math.round(raw) : Math.round(raw * 100) / 100;

    return {
        value,
        kind,
        direction: value > 0 ? 1 : value < 0 ? -1 : 0,
    };
};

export const formatAbsoluteNumber = (value: number): string =>
    Math.abs(value).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).replace('-', '');

// Текст для дельты без стрелки и без знака — направление показывает иконка ComparisonDelta.
export const formatDeltaText = (delta: Delta): string => {
    if (delta.direction === 0) {
        return '';
    }

    if (delta.kind === DeltaKind.RANK) {
        return String(Math.abs(delta.value));
    }

    const suffix = delta.kind === DeltaKind.POINTS ? ' п.п.' : '';

    return `${formatAbsoluteNumber(delta.value)}${suffix}`;
};

export const getPeriodLabel = (period: ComparisonDateRange): string =>
    `${dayjs(formatPeriodDate(period.dateFrom)).format('DD.MM.YYYY')} – ${dayjs(formatPeriodDate(period.dateTo)).format(
        'DD.MM.YYYY',
    )}`;
